import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { testimonials } from '../data/mock';
import { Button } from './ui/button';
import { Card } from './ui/card';

const TestimonialSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalPages);
    }, 6000);
    return () => clearInterval(timer);
  }, [totalPages]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const getCurrentTestimonials = () => {
    const startIndex = currentIndex * itemsPerPage;
    return testimonials.slice(startIndex, startIndex + itemsPerPage);
  };

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-gray-600">
            Trusted by thousands of businesses nationwide
          </p>
        </div>

        <div className="relative">
          {/* Desktop: 3 cards */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {getCurrentTestimonials().map((testimonial) => (
              <Card key={testimonial.id} className="p-6 bg-white shadow-xl rounded-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="flex justify-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-gray-700 text-center mb-6 italic line-clamp-4">
                  "{testimonial.text}"
                </p>
                
                <div className="text-center mt-auto">
                  <p className="font-bold text-slate-900">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-600 text-sm">{testimonial.designation}</p>
                </div>
              </Card>
            ))}
          </div>

          {/* Mobile: 1 card */}
          <div className="md:hidden">
            <Card className="p-8 bg-white shadow-xl rounded-2xl">
              <div className="flex justify-center mb-4">
                {[...Array(getCurrentTestimonials()[0].rating)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              
              <p className="text-lg text-gray-700 text-center mb-6 italic">
                "{getCurrentTestimonials()[0].text}"
              </p>
              
              <div className="text-center">
                <p className="font-bold text-slate-900 text-lg">
                  {getCurrentTestimonials()[0].name}
                </p>
                <p className="text-gray-600">{getCurrentTestimonials()[0].designation}</p>
              </div>
            </Card>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <Button
              onClick={prevSlide}
              variant="outline"
              size="icon"
              className="rounded-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-3 w-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-teal-600 w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
            <Button
              onClick={nextSlide}
              variant="outline"
              size="icon"
              className="rounded-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSlider;